import React, { useEffect, useState } from "react";
import { Loading } from "@/components/LoadingState";
import { Bell, BellRing, Check, X, Trash2, RefreshCw, Smartphone, AlertCircle, Receipt, Award, Scale, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { SecurityAlertsPanel } from "@/components/SecurityAlertsPanel";
import { AccessDeniedPanel } from "@/components/AccessDeniedPanel";
import { isPushSupported, getNotificationPermission, requestAndSubscribe, unsubscribePush } from "@/lib/push";

const TYPE_META = {
  avans: { label: "Avans", color: "amber", Icon: Receipt },
  kidem: { label: "Kıdem", color: "emerald", Icon: Award },
  prim: { label: "Prim", color: "indigo", Icon: Scale },
  diyanet_sync: { label: "Diyanet Senkron", color: "rose", Icon: CalendarCheck },
};

const toneClass = (color) => ({
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  indigo: "bg-indigo-50 border-indigo-200 text-indigo-800",
  rose: "bg-rose-50 border-rose-200 text-rose-800",
}[color] || "bg-slate-50 border-slate-200 text-slate-800");

const dotClass = (color) => ({
  amber: "bg-amber-500",
  emerald: "bg-emerald-500",
  indigo: "bg-indigo-500",
  rose: "bg-rose-500",
}[color] || "bg-slate-500");

export default function BildirimTab() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [permission, setPermission] = useState("default");
  const [filter, setFilter] = useState("pending"); // pending | all

  const load = async () => {
    setLoading(true);
    try {
      const qs = filter === "all" ? "" : `?status=pending`;
      const r = await api.get(`/notifications${qs}`);
      setRows(r.data.rows || []);
    } catch {
      toast.error("Bildirimler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    getNotificationPermission().then(setPermission);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const scanNow = async () => {
    setScanning(true);
    try {
      const r = await api.post("/notifications/scan?days_ahead=1");
      toast.success(`${r.data.created} yeni bildirim oluşturuldu`);
      await load();
    } catch {
      toast.error("Tarama başarısız");
    } finally {
      setScanning(false);
    }
  };

  const enablePush = async () => {
    try {
      await requestAndSubscribe();
      setPermission("granted");
      toast.success("Push bildirimleri etkinleştirildi");
    } catch (e) {
      toast.error(`Push etkinleştirilemedi: ${e.message || e}`);
    }
  };

  const disablePush = async () => {
    await unsubscribePush();
    setPermission(await getNotificationPermission());
    toast.success("Push bildirimleri kapatıldı");
  };

  const approve = async (notif) => {
    if (!window.confirm(`"${notif.title}" bildirimi tüm abone cihazlara gönderilsin mi?`)) return;
    try {
      const r = await api.post(`/notifications/${notif.id}/approve`);
      const d = r.data;
      if (d.delivered > 0) toast.success(`${d.delivered} cihaza bildirim gönderildi`);
      else toast.success("Onaylandı (abone cihaz bulunamadı)");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onaylanamadı");
    }
  };

  const dismiss = async (notif) => {
    await api.post(`/notifications/${notif.id}/dismiss`);
    toast.success("Bildirim iptal edildi");
    load();
  };

  const del = async (notif) => {
    if (!window.confirm("Bildirim kaydı silinsin mi?")) return;
    await api.delete(`/notifications/${notif.id}`);
    toast.success("Silindi");
    load();
  };

  const supported = isPushSupported();

  return (
    <div className="space-y-4">
      {isAdmin && <SecurityAlertsPanel onChanged={() => window.dispatchEvent(new Event("bordromat:security-alerts-changed"))} />}
      {/* Push permission banner */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-5 shadow-sm" data-testid="push-permission-banner">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`shrink-0 w-10 h-10 rounded-lg grid place-items-center ${permission === "granted" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
              {permission === "granted" ? <BellRing size={18} /> : <Bell size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 flex items-center gap-2">
                Push Bildirimi
                {permission === "granted" && (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700">Aktif</span>
                )}
                {permission === "denied" && (
                  <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-700">Reddedildi</span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {!supported && "Tarayıcınız push bildirimi desteklemiyor. PWA'yı ana ekrana ekleyin."}
                {supported && permission === "default" && "Yönetici onayladığı bildirimleri telefonunuza almak için izin verin."}
                {supported && permission === "granted" && "Bu cihaz push bildirimlerini alır. Yönetici onayladığında gelir."}
                {supported && permission === "denied" && "Tarayıcı ayarlarından bildirime izin verin."}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {supported && permission !== "granted" && (
              <button
                data-testid="enable-push-button"
                onClick={enablePush}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-lg text-xs font-semibold"
              >
                <Smartphone size={14} /> İzin Ver
              </button>
            )}
            {supported && permission === "granted" && (
              <button
                data-testid="disable-push-button"
                onClick={disablePush}
                className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2 rounded-lg text-xs font-semibold"
              >
                Kapat
              </button>
            )}
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 md:p-5 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h2 className="font-display text-lg md:text-xl font-bold text-slate-900 flex items-center gap-2">
              <Bell size={18} /> Bildirim Kuyruğu
            </h2>
            <p className="text-xs md:text-sm text-slate-500 mt-0.5">
              Yarınki avans/kıdem/prim ödemeleri · yönetici onayı gerektirir
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs" data-testid="filter-toggle">
              <button
                onClick={() => setFilter("pending")}
                className={`px-3 py-2 font-semibold ${filter === "pending" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                data-testid="filter-pending"
              >Bekleyen</button>
              <button
                onClick={() => setFilter("all")}
                className={`px-3 py-2 font-semibold ${filter === "all" ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
                data-testid="filter-all"
              >Tümü</button>
            </div>
            <button
              data-testid="scan-notifications-button"
              onClick={scanNow}
              disabled={scanning}
              className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 rounded-lg text-xs font-semibold disabled:opacity-50"
            >
              <RefreshCw size={14} className={scanning ? "animate-spin" : ""} /> {scanning ? "Taranıyor..." : "Yeni Tara"}
            </button>
          </div>
        </div>

        <div className="divide-y divide-slate-100" data-testid="notifications-list">
          {loading && <Loading text="Bildirimler yükleniyor..." />}
          {!loading && rows.length === 0 && (
            <div className="py-10 text-center text-slate-400 text-sm">
              <AlertCircle className="mx-auto mb-2" size={28} />
              {filter === "pending" ? "Bekleyen bildirim yok" : "Hiç bildirim kaydı yok"}. Yeni tarama yapın.
            </div>
          )}
          {rows.map((n) => {
            const meta = TYPE_META[n.type] || { label: n.type, color: "slate", Icon: Bell };
            const Icon = meta.Icon;
            const isPending = n.status === "pending";
            return (
              <div key={n.id} data-testid={`notif-row-${n.id}`} className="p-4 md:p-5 flex items-start gap-3">
                <div className={`shrink-0 w-10 h-10 rounded-lg border ${toneClass(meta.color)} grid place-items-center`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${toneClass(meta.color)}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${dotClass(meta.color)}`}></span>
                      {meta.label}
                    </span>
                    <StatusPill status={n.status} />
                    <span className="text-xs text-slate-500 font-mono-tab">{n.due_date}</span>
                  </div>
                  <div className="font-semibold text-slate-900 mt-1 truncate">{n.title}</div>
                  <div className="text-sm text-slate-600 mt-0.5">{n.body}</div>
                </div>
                <div className="shrink-0 flex items-center gap-1">
                  {isPending && (
                    <>
                      <button
                        data-testid={`approve-${n.id}`}
                        onClick={() => approve(n)}
                        className="w-9 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center justify-center"
                        title="Onayla ve Gönder"
                      ><Check size={14} /></button>
                      <button
                        data-testid={`dismiss-${n.id}`}
                        onClick={() => dismiss(n)}
                        className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 inline-flex items-center justify-center"
                        title="İptal"
                      ><X size={14} /></button>
                    </>
                  )}
                  <button
                    data-testid={`delete-notif-${n.id}`}
                    onClick={() => del(n)}
                    className="w-9 h-9 rounded-lg hover:bg-rose-50 text-rose-500 inline-flex items-center justify-center"
                    title="Sil"
                  ><Trash2 size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      {isAdmin && <AccessDeniedPanel />}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: ["bg-amber-100 text-amber-700", "Bekliyor"],
    approved: ["bg-blue-100 text-blue-700", "Onaylı"],
    sent: ["bg-emerald-100 text-emerald-700", "Gönderildi"],
    dismissed: ["bg-slate-200 text-slate-600", "İptal"],
    failed: ["bg-rose-100 text-rose-700", "Hata"],
  };
  const [cls, label] = map[status] || ["bg-slate-100 text-slate-600", status];
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>{label}</span>;
}
